import { type FC, useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAutocompletePromocodeCampaignsQuery } from "@/partner/entities/promocodes";

interface CampaignOption {
  value: string;
  label: string;
}

interface PromocodeCampaignsAutocompleteProps {
  label: string;
  value: string[];
  onChange: (campaignIds: string[]) => void;
  multiple?: boolean;
  /** Task 8: форма промокода — поле блокируется при вводе новой кампании
   * (Switch "новая кампания"). */
  disabled?: boolean;
  /** Task 8: обязательна для масс-генерации (PromocodesMultipleForm), пока не
   * включён режим создания новой кампании — required прокидывается в
   * нативный input автокомплита (HTML5-валидация формы на submit). */
  required?: boolean;
}

// Тот же паттерн, что и ProductsAutocomplete: управляемый value/onChange
// (без react-final-form), значение живёт снаружи (URL-фильтр или форма).
// При multiple={false} (Task 8: форма промокода — одна кампания) наружный
// интерфейс всё равно остаётся массивом из 0–1 элементов.
export const PromocodeCampaignsAutocomplete: FC<
  PromocodeCampaignsAutocompleteProps
> = ({ label, value, onChange, multiple = true, disabled, required }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  // Дебаунс 400мс перед отправкой в запрос — ввод в поле остаётся мгновенным
  // (setSearch не тормозим), а сетевой запрос уходит только через 400мс
  // простоя (тот же приём, что и PromocodesFilter.tsx).
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  const { data, loading } = useAutocompletePromocodeCampaignsQuery({
    fetchPolicy: "network-only",
    variables: { input: debouncedSearch || undefined, value },
  });

  const optionsById = new Map<string, CampaignOption>();
  for (const option of data?.autocompletePromocodeCampaigns?.options ?? []) {
    if (option) optionsById.set(option.value, option);
  }
  for (const option of data?.autocompletePromocodeCampaigns?.value ?? []) {
    if (option) optionsById.set(option.value, option);
  }
  const options = Array.from(optionsById.values());

  if (!multiple) {
    const selected =
      options.find((option) => option.value === value[0]) ?? null;

    // Без freeSolo: произвольный текст здесь бессмыслен (у него нет id
    // кампании, новая кампания заводится тумблером рядом), а freeSolo прятал
    // стрелку и список, и поле выглядело обычным текстовым (баг 2026-09-30).
    return (
      <Autocomplete<CampaignOption, false, false, false>
        openOnFocus
        autoComplete
        autoHighlight
        filterSelectedOptions
        fullWidth
        disabled={disabled}
        filterOptions={(opts) => opts}
        loading={loading}
        options={options}
        noOptionsText={t("promocodes.form.campaignNoOptions")}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, selectedOption) =>
          option.value === selectedOption.value
        }
        value={selected}
        onInputChange={(_event, newInputValue, reason) => {
          setSearch(reason === "reset" ? "" : newInputValue);
        }}
        onChange={(_event, newValue) => {
          onChange(newValue ? [newValue.value] : []);
        }}
        renderInput={(params) => (
          <TextField {...params} label={label} required={required} />
        )}
      />
    );
  }

  return (
    <Autocomplete<CampaignOption, true, false, true>
      multiple
      freeSolo
      openOnFocus
      clearOnBlur
      autoComplete
      autoHighlight
      filterSelectedOptions
      fullWidth
      disabled={disabled}
      filterOptions={(opts) => opts}
      loading={loading}
      options={options}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.label
      }
      isOptionEqualToValue={(option, selectedOption) =>
        option.value === selectedOption.value
      }
      value={options.filter((option) => value.includes(option.value))}
      onInputChange={(_event, newInputValue, reason) => {
        setSearch(reason === "reset" ? "" : newInputValue);
      }}
      onChange={(_event, newValue) => {
        onChange(
          newValue
            .filter((option): option is CampaignOption => {
              return typeof option !== "string";
            })
            .map((option) => option.value)
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} required={required} />
      )}
    />
  );
};
